import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout';
import MetricCard from '../components/MetricCard';
import QuickActionCard from '../components/QuickActionCard';
import PetStatusOverview from '../components/PetStatusOverview';
import { 
  SubmitRequestIcon, 
  TrackPetIcon, 
  MaterialsIcon, 
  RewardsIcon, 
  CashValueIcon,
  TotalReferralsIcon,
  BaselineCreditIcon,
  TotalRedeemedIcon
} from '../components/CustomIcons';
import { toast } from 'sonner';
import { History, ChevronDown } from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [petStats, setPetStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const authFetch = async (url, options = {}) => {
    const token = localStorage.getItem('vet_portal_token'); // Correct key!
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (response.status === 401) {
      localStorage.removeItem('vet_portal_token');
      localStorage.removeItem('vet_portal_clinic');
      localStorage.setItem('session_expired', 'true');
      navigate('/login');
      throw new Error('Unauthorized');
    }

    return response;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch dashboard data
        const dashboardResponse = await authFetch('/api/dashboard');
        if (dashboardResponse.ok) {
          const data = await dashboardResponse.json();
          setDashboardData(data);
        }

        // Fetch pet status
        const petsResponse = await authFetch('/api/pets/status');
        if (petsResponse.ok) {
          const petsData = await petsResponse.json();
          setPetStats(petsData);
        }

        // Fetch recent events for notifications
        try {
          const eventsResponse = await authFetch('/api/events/recent');
          if (eventsResponse.ok) {
            const eventsData = await eventsResponse.json();
            const events = eventsData.events || [];
            events.forEach(event => {
              if (!event.read && event.event_type) {
                const eventIcons = {
                  'pet_collected_from_clinic': '🚗',
                  'pet_in_care': '🏥',
                  'pet_cremation_started': '💧',
                  'pet_completed': '✅',
                  'ashes_delivered_to_clinic': '⚱️',
                  'redemption_processed': '💰',
                  'product_order_fulfilled': '📦'
                };
                const icon = eventIcons[event.event_type] || '📬';
                if (event.event_type === 'product_order_fulfilled') {
                  toast.success(`${icon} ${event.message}`, {
                    duration: 8000,
                    description: 'Your product order has been dispatched!'
                  });
                } else if (event.event_type === 'redemption_processed') {
                  toast.success(`${icon} ${event.message}`, {
                    duration: 8000,
                    description: 'Funds transferred to your bank account'
                  });
                } else {
                  toast.success(`${icon} ${event.message}`, {
                    duration: 5000,
                    description: event.pet_name
                  });
                }
              }
            });
          }
        } catch (e) {
          // Silently fail event polling
        }
      } catch (error) {
        if (error.message !== 'Unauthorized') {
          toast.error('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  // Calculate actual month-over-month referral growth from pet arrival dates
  const referralTrend = React.useMemo(() => {
    if (!petStats?.pets?.length) return null;
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    
    let thisMonth = 0;
    let lastMonth = 0;
    petStats.pets.forEach(pet => {
      if (!pet.arrival_date) return;
      const d = new Date(pet.arrival_date);
      if (d >= thisMonthStart) thisMonth++;
      else if (d >= lastMonthStart && d < thisMonthStart) lastMonth++;
    });
    
    if (lastMonth === 0 && thisMonth === 0) return null;
    if (lastMonth === 0) return { direction: 'up', value: `+${thisMonth}`, label: `new this month` };
    const pct = Math.round(((thisMonth - lastMonth) / lastMonth) * 100);
    if (pct === 0) return { direction: 'up', value: '0%', label: 'vs last month' };
    return {
      direction: pct >= 0 ? 'up' : 'down',
      value: `${pct >= 0 ? '+' : ''}${pct}%`,
      label: 'vs last month'
    };
  }, [petStats]);

  // Recalculate pet stats from the pets list using timestamp-driven logic
  // so counts always match the displayed card statuses
  const computedPetStats = React.useMemo(() => {
    if (!petStats?.pets) return petStats;
    const pets = petStats.pets;
    let inCare = 0, completed = 0, pending = 0, prepayment = 0;
    pets.forEach(pet => {
      if (pet.workflow_stage === 'prepayment') { prepayment++; return; }
      const ashesToVet = pet.ashes_delivery_to_vet === true;
      if (pet.completed_at || (ashesToVet && pet.delivered_at)) {
        completed++;
      } else if (pet.cremation_started_at || pet.in_care_at || pet.collected_at) {
        inCare++;
      } else {
        pending++;
      }
    });
    return { ...petStats, pets_in_care: inCare, pets_completed: completed, pets_pending: pending, pets_prepayment: prepayment };
  }, [petStats]);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-8 animate-fade-in">
          {/* Header Skeleton */}
          <div className="space-y-2">
            <div className="h-10 w-64 skeleton rounded-lg"></div>
            <div className="h-5 w-96 skeleton rounded-lg"></div>
          </div>

          {/* Metrics Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 skeleton rounded-2xl"></div>
            ))}
          </div>

          {/* Quick Actions Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 skeleton rounded-2xl"></div>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const quickActions = [
    {
      title: 'Submit Pet Collection',
      description: 'Request a pet collection for water cremation services',
      icon: SubmitRequestIcon,
      color: 'primary',
      onClick: () => navigate('/requests/new'),
      featured: true  // Add neon border animation
    },
    {
      title: 'Track Pet Status',
      description: 'View real-time status of your referred pets',
      icon: TrackPetIcon,
      color: 'accent',
      onClick: () => navigate('/pet-tracking')
    },
    {
      title: 'Request Materials',
      description: 'Order pet tags and brochures for your clinic',
      icon: MaterialsIcon,
      color: 'success',
      onClick: () => navigate('/material-request')
    }
  ];

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-2"
        >
          <h1 className="text-4xl font-bold tracking-tight text-primary" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-lg text-muted-foreground">
            {dashboardData?.clinic_name || 'Welcome back'}
          </p>
        </motion.div>

        {/* Promo Message Banner — themed based on occasion */}
        {dashboardData?.promo_message && (() => {
          const themes = {
            general:     { bg: 'bg-amber-100',   border: 'border-amber-300',   text: 'text-amber-900',   emoji: '🎈' },
            easter:      { bg: 'bg-purple-100',  border: 'border-purple-300',  text: 'text-purple-900',  emoji: '🐣' },
            christmas:   { bg: 'bg-red-100',     border: 'border-red-300',     text: 'text-red-900',     emoji: '🎄' },
            vetday:      { bg: 'bg-teal-100',    border: 'border-teal-300',    text: 'text-teal-900',    emoji: '🐾' },
            newyear:     { bg: 'bg-yellow-100',  border: 'border-yellow-300',  text: 'text-yellow-900',  emoji: '🎆' },
            appreciation:{ bg: 'bg-blue-100',    border: 'border-blue-300',    text: 'text-blue-900',    emoji: '💙' },
          };
          const t = themes[dashboardData.banner_theme] || themes.general;
          return (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
              className={`w-fit flex items-center gap-3 px-5 py-3 rounded-2xl ${t.bg} border ${t.border} shadow-sm`}>
              <span className="flex-shrink-0 text-lg leading-none">{t.emoji}</span>
              <p className={`text-sm font-medium ${t.text}`}>{dashboardData.promo_message}</p>
            </motion.div>
          );
        })()}

        {/* Quick Actions - MOVED TO TOP */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h2 className="text-2xl font-semibold mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Quick Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {quickActions.map((action, index) => (
              <QuickActionCard
                key={action.title}
                {...action}
                delay={0.2 + (index * 0.1)}
              />
            ))}
          </div>
        </motion.div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <MetricCard
            title="Reward Points"
            value={dashboardData?.current_points || 0}
            subtitle="5 points per referral"
            icon={RewardsIcon}
            accentColor="primary"
            delay={0.5}
          />
          <MetricCard
            title="Cash Value"
            value={`$${(dashboardData?.cash_value || 0).toFixed(2)}`}
            subtitle="$1 per point"
            icon={CashValueIcon}
            accentColor="success"
            delay={0.6}
          />
          <MetricCard
            title="Total Referrals"
            value={dashboardData?.total_referrals || 0}
            subtitle="Pet collection requests"
            icon={TotalReferralsIcon}
            accentColor="accent"
            delay={0.7}
          />
        </div>

        {/* Pet Status Overview — collapsible on mobile, default collapsed */}
        {computedPetStats && (
          <MobilePetOverview petStats={computedPetStats} />
        )}

        {/* Additional Stats Card — hidden on mobile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.9 }}
          className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          <div className="p-6 rounded-2xl border-0 shadow-md bg-gradient-to-br from-primary/5 to-primary/10 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-4">
              <BaselineCreditIcon size={80} className="h-20 w-20" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Baseline Credits</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {dashboardData?.baseline_credits || 0}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-2xl border-0 shadow-md bg-gradient-to-br from-accent/5 to-accent/10 hover:shadow-lg transition-shadow duration-300">
            <div className="flex items-center gap-4">
              <TotalRedeemedIcon size={80} className="h-20 w-20" />
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Redeemed</p>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {dashboardData?.total_redeemed || 0}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
};

// Mobile-collapsible Pet Status Overview (collapsed by default)
const MobilePetOverview = ({ petStats }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Mobile: collapsible */}
      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full flex items-center justify-between p-4 rounded-2xl border-0 shadow-md bg-card cursor-pointer hover:shadow-lg transition-all"
        >
          <span className="font-semibold text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Pet Status — {petStats.pets_in_care || 0} In Our Care · {petStats.pets_completed || 0} Completed · {petStats.pets_pending || 0} Pending
          </span>
          <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.3 }}>
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          </motion.div>
        </button>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="overflow-hidden mt-2">
            <PetStatusOverview stats={petStats} delay={0} />
          </motion.div>
        )}
      </div>
      {/* Desktop: always visible */}
      <div className="hidden md:block">
        <PetStatusOverview stats={petStats} delay={0.8} />
      </div>
    </>
  );
};

export default Dashboard;
